import React, { useRef, useState } from 'react';
import './ActionButton.css';

export interface ActionButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'children'> {
  /** Label shown when idle. */
  label: string;
  /** Label shown while busy (e.g. "Saving..."). Reserves width so the button never resizes. */
  busyLabel?: string;
  /** Caller-owned busy state (e.g. form `loading`). */
  busy?: boolean;
  /** When the handler returns a promise, further clicks are ignored until it settles. */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<unknown>;
}

export const ActionButton: React.FC<ActionButtonProps> = ({
  label,
  busyLabel,
  busy = false,
  onClick,
  disabled,
  className = 'app-btn app-btn-primary',
  type = 'button',
  ...rest
}) => {
  const inFlight = useRef(false);
  const [pending, setPending] = useState(false);
  const isBusy = busy || pending;
  const activeLabel = isBusy && busyLabel ? busyLabel : label;
  const sizerLabel = isBusy && busyLabel ? label : busyLabel ?? '';

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (inFlight.current || isBusy || !onClick) return;
    const result = onClick(event);
    if (!result || typeof (result as Promise<unknown>).then !== 'function') return;
    inFlight.current = true;
    setPending(true);
    try {
      await result;
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };

  return (
    <button
      {...rest}
      type={type}
      className={`${className} app-action-btn`}
      disabled={disabled || isBusy}
      aria-busy={isBusy || undefined}
      onClick={handleClick}
    >
      <span className="app-action-btn-label" data-sizer={sizerLabel}>
        <span className="app-action-btn-text">{activeLabel}</span>
      </span>
    </button>
  );
};

export default ActionButton;
